import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import VastuSubHeader from '../components/VastuSubHeader';
import PageBackground from '../components/PageBackground';
import ReviewModal from './Dashboard/ReviewModal'; // <-- IMPORT MODAL
import toast from 'react-hot-toast';
// import './VastuStyles.css';
import './MyConsultationsPage.css'

// --- 1. ADD THIS HELPER FUNCTION AT THE TOP ---
// --- UPDATED TRANSPARENT STATUS HELPER ---
const getUserFriendlyStatus = (rawStatus) => {
  if (!rawStatus) return 'Pending';

  const statusMap = {
    payment_pending: 'Payment Pending',
    payment_submitted: 'Payment Submitted',
    paid: 'Paid',
    under_review: 'Under Review',
    more_info_needed: 'More Info Needed',
    scheduled: 'Scheduled',
    completed: 'Completed',
    report_uploaded: 'Report Uploaded',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
  };

  const key = String(rawStatus).toLowerCase().trim();

  // Return the mapped label, or automatically capitalize and format any custom status
  return statusMap[key] || 
    key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDate = (date) => date ? new Date(date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Not scheduled';
const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

const MyConsultationsPage = () => {
  const { isLoggedIn, user } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // UI States
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeTabs, setActiveTabs] = useState({}); // Track which tab is open for each card
  const [qrPreviewUrls, setQrPreviewUrls] = useState({});
  const qrPreviewUrlsRef = useRef({});

  // Action States
  const [uploadingId, setUploadingId] = useState('');
  const [submittingProofId, setSubmittingProofId] = useState('');
  const [sendingMessageId, setSendingMessageId] = useState('');
  const [isNotifyingId, setIsNotifyingId] = useState(''); // <-- NEW STATE FOR NOTIFY BUTTON
  
  // Form Drafts
  const [selectedFiles, setSelectedFiles] = useState({});
  const [proofDrafts, setProofDrafts] = useState({});
  const [messageDrafts, setMessageDrafts] = useState({}); // Holds text and file for the chat
  
  // Review Modal State
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [consultationToReview, setConsultationToReview] = useState(null);

  // --- DATA LOADING ---
  const loadData = () => {
    setIsLoading(true);
    apiService.getMyVastuConsultations()
      .then((response) => setConsultations(response.data || []))
      .catch((err) => toast.error(err.message || 'Failed to load consultations'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isLoggedIn) loadData();
  }, [isLoggedIn]);

  useEffect(() => () => {
    Object.values(qrPreviewUrlsRef.current).forEach((url) => {
      if (url) window.URL.revokeObjectURL(url);
    });
  }, []);

  // --- ACCORDION & TABS ---
  const toggleExpand = (item) => {
    if (expandedCardId === item._id) {
      setExpandedCardId(null);
    } else {
      setExpandedCardId(item._id);
      // Default to the 'details' tab when opening
      setActiveTabs(prev => ({ ...prev, [item._id]: prev[item._id] || 'details' }));
    }
  };

  const setTab = (consultationId, tabName) => {
    setActiveTabs(prev => ({ ...prev, [consultationId]: tabName }));
    
    // Auto-load QR preview if switching to payment tab and it exists
    if (tabName === 'payment') {
        const item = consultations.find(c => c._id === consultationId);
        if (item?.paymentInstructions?.qrCodeFile && !qrPreviewUrlsRef.current[item._id]) {
            loadPaymentQrPreview(item);
        }
    }
  };

  // --- REVIEW HANDLERS ---
  const handleOpenReview = (consultation) => {
    setConsultationToReview(consultation);
    setIsReviewModalOpen(true);
  };

  const handleSubmitReview = async (consultationId, payload) => {
    try {
      const response = await apiService.submitVastuReview(consultationId, payload);
      if (response.success) {
        toast.success("Review saved successfully!");
        setConsultations(current => current.map(item => item._id === consultationId ? response.data : item));
        setIsReviewModalOpen(false);
      }
    } catch (err) { toast.error(err.message || "Failed to submit review."); }
  };

  // --- CHAT/MESSAGE HANDLERS ---
  const handleMessageDraftChange = (consultationId, field, value) => {
    setMessageDrafts(prev => ({
        ...prev,
        [consultationId]: { ...(prev[consultationId] || {}), [field]: value }
    }));
  };

  const sendMessage = async (consultationId) => {
    const draft = messageDrafts[consultationId] || {};
    const text = (draft.text || '').trim();
    
    if (!text && !draft.file) {
        toast.error("Please enter a message or attach a file.");
        return;
    }

    const formData = new FormData();
    if (text) formData.append('text', text);
    if (draft.file) formData.append('attachment', draft.file);

    setSendingMessageId(consultationId);
    try {
        const response = await apiService.addVastuConsultationMessage(consultationId, formData);
        toast.success("Message sent.");
        setConsultations(current => current.map(item => item._id === consultationId ? response.data : item));
        // Clear draft
        setMessageDrafts(prev => ({ ...prev, [consultationId]: { text: '', file: null } }));
    } catch (err) {
        toast.error(err.message || "Failed to send message.");
    } finally {
        setSendingMessageId('');
    }
  };

  const updateProofDraft = (consultationId, field, value) => {
    setProofDrafts(prev => ({
        ...prev,
        [consultationId]: { ...(prev[consultationId] || {}), [field]: value }
    }));
  };

  // --- NEW HANDLER: NOTIFY CONSULTANT ---
  const handleNotifyConsultant = async (consultationId) => {
    setIsNotifyingId(consultationId);
    try {
      const response = await apiService.notifyVastuConsultantNewInfo(consultationId);
      if (response.success) {
        toast.success("Consultant notified! Status updated to Under Review.");
        setConsultations(current => current.map(item => item._id === consultationId ? response.data : item));
      }
    } catch (err) {
      toast.error(err.message || "Failed to notify consultant.");
    } finally {
      setIsNotifyingId('');
    }
  };

  // --- FILE & PAYMENT HANDLERS ---
  const uploadMoreFiles = async (consultationId) => {
    const files = selectedFiles[consultationId] || [];
    if (!files.length) { toast.error('Please choose a file to upload.'); return; }
    const formData = new FormData();
    files.forEach((file) => formData.append('floorPlans', file));
    setUploadingId(consultationId);
    try {
      const response = await apiService.uploadVastuConsultationFiles(consultationId, formData);
      setConsultations(current => current.map(item => item._id === consultationId ? response.data : item));
      setSelectedFiles(current => ({ ...current, [consultationId]: [] }));
      toast.success('File uploaded successfully.');
    } catch (err) { toast.error(err.message); } finally { setUploadingId(''); }
  };

  const downloadFile = async (consultationId, file) => {
    try { await apiService.downloadMyVastuConsultationFile(consultationId, file._id, file.originalName); } 
    catch (err) { toast.error(err.message); }
  };

  const loadPaymentQrPreview = async (item) => {
    try {
      const previewUrl = await apiService.getMyPaymentQrPreviewUrl(item._id);
      qrPreviewUrlsRef.current = { ...qrPreviewUrlsRef.current, [item._id]: previewUrl };
      setQrPreviewUrls(qrPreviewUrlsRef.current);
    } catch (err) { console.error('Failed to load QR preview', err); }
  };

  const submitPaymentProof = async (consultationId) => {
    const draft = proofDrafts[consultationId] || {};
    const transactionReference = String(draft.transactionReference || '').trim();
    if (!transactionReference && !draft.proofFile) { toast.error('Enter transaction reference or upload screenshot.'); return; }
    const formData = new FormData();
    formData.append('transactionReference', transactionReference);
    if (draft.proofFile) formData.append('proofFile', draft.proofFile);

    setSubmittingProofId(consultationId);
    try {
      const response = await apiService.submitPaymentProof(consultationId, formData);
      setConsultations(current => current.map(item => item._id === consultationId ? response.data : item));
      setProofDrafts(current => ({ ...current, [consultationId]: {} }));
      toast.success('Payment proof submitted.');
    } catch (err) { toast.error(err.message); } finally { setSubmittingProofId(''); }
  };


  // --- RENDER HELPERS ---
  if (!isLoggedIn) return <div className="vastu-page"><div className="empty-state">Please login to view your consultations.</div></div>;
  const bgUrl = 'https://images.unsplash.com/photo-1558535235-943a5c518b2b?auto=format&fit=crop&w=1920&q=80';

  return (
    <div className="vastu-page">
      <PageBackground imageUrl={bgUrl} />
      <VastuSubHeader />

      <div className="vastu-consultations-content-wrapper" style={{ marginTop: '2rem' }}>
        <div className="section-heading">
          <h1>My Vastu Consultations</h1>
          {/* <p>Track your review status, communicate with your consultant, and manage payments.</p> */}
        </div>

        {isLoading ? (
          <div className="loading-spinner">Loading consultations...</div>
        ) : consultations.length === 0 ? (
          <div className="empty-state">You have no Vastu consultations yet.</div>
        ) : (
          <div className="consultation-accordion-list">
            {consultations.map((item) => {
              const isExpanded = expandedCardId === item._id;
              const activeTab = activeTabs[item._id] || 'details';
              
              const displayStatus = getUserFriendlyStatus(item.status);
              
              return (
                <article className={`consultation-accordion-card ${isExpanded ? 'expanded' : ''}`} key={item._id}>
                  
                  {/* --- CARD HEADER --- */}
                  <div className="accordion-header" onClick={() => toggleExpand(item)}>
                    <div className="header-primary-info">
                      <div className="booking-code">{item.bookingCode}</div>
                      <h2 className="consultation-type">{item.consultationType?.name}</h2>
                      <p className="property-brief">{item.property?.city || 'Property'}, {item.property?.propertyType}</p>
                    </div>
                    <div className="header-secondary-info">
                      <span className={`status-pill status-${displayStatus.toLowerCase().replace(/ /g, '-')}`}>
                        {displayStatus}
                      </span>
                      <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} expand-icon`}></i>
                    </div>
                  </div>

                  {/* --- CARD BODY (EXPANDED) --- */}
                  {isExpanded && (
                    <div className="accordion-body">
                      
                      {/* --- TABS NAVIGATION --- */}
                      <div className="consultation-tabs">
                          <button className={activeTab === 'details' ? 'active' : ''} onClick={() => setTab(item._id, 'details')}>
                             <i className="fas fa-info-circle"></i> Details & Files
                          </button>
                          <button className={activeTab === 'chat' ? 'active' : ''} onClick={() => setTab(item._id, 'chat')}>
                             <i className="fas fa-comments"></i> Discussion Thread
                          </button>
                          <button className={activeTab === 'payment' ? 'active' : ''} onClick={() => setTab(item._id, 'payment')}>
                             <i className="fas fa-wallet"></i> Payment
                             {item.status === 'payment_pending' && <span className="tab-alert-dot"></span>}
                          </button>
                      </div>

                      <div className="tab-content">
                          
                          {/* ==================================== */}
                          {/* TAB 1: DETAILS & FILES               */}
                          {/* ==================================== */}
                          {activeTab === 'details' && (
                              <div className="fade-in">
                                  {/* --- REMEDIES SUMMARY (If available) --- */}
                                  {item.remediesSummary && (
                                    <div className="remedies-summary-box">
                                        <h4><i className="fas "></i> Consultant's Remedies Summary</h4>
                                        <p>{item.remediesSummary}</p>
                                    </div>
                                  )}

                                  <div className="detail-grid">
                                    <div className="detail-item">
                                      <span>Consultant</span>
                                      <strong>{item.consultant?.displayName || 'To be assigned'}</strong>
                                    </div>
                                    <div className="detail-item">
                                      <span>Meeting Status</span>
                                      {['completed', 'report_uploaded', 'cancelled', 'refunded'].includes(item.status) ? (
                                        <>
                                          <strong style={{ color: item.status === 'cancelled' || item.status === 'refunded' ? '#dc3545' : 'var(--vastu-green)' }}>
                                            {item.status === 'report_uploaded' ? 'Report Uploaded' : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                          </strong>
                                          <small style={{ color: '#666', marginTop: '4px' }}>
                                            Consultation has concluded.
                                          </small>
                                        </>
                                      ) : (
                                        <>
                                          <strong>{formatDate(item.scheduledAt)}</strong>
                                          {item.meetingLink && (
                                            <a href={item.meetingLink} target="_blank" rel="noreferrer" className="meeting-link-btn">
                                              <i className="fas fa-video"></i> Join Video Call
                                            </a>
                                          )}
                                        </>
                                      )}
                                    </div>
                                    
                                    {/* --- RATE & REVIEW BUTTON --- */}
                                    <div className="detail-item">
                                        <span>Consultation Feedback</span>
                                        {['completed', 'report_uploaded'].includes(item.status) ? (
                                            item.rating?.score ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                                                    <span style={{ color: '#f59e0b', fontSize: '1.2rem', textTransform: 'none' }}>
                                                    {'★'.repeat(item.rating.score)}{'☆'.repeat(5 - item.rating.score)}
                                                    </span>
                                                    <button type="button" className="text-action-btn" onClick={() => handleOpenReview(item)}>
                                                        Edit Review
                                                    </button>
                                                </div>
                                            ) : (
                                                <button type="button" className="primary-btn" onClick={() => handleOpenReview(item)} style={{ marginTop: '5px', padding: '0.5rem' }}>
                                                    Rate & Review
                                                </button>
                                            )
                                        ) : (
                                            <strong><small className="text-muted">Available after completion</small></strong>
                                        )}
                                    </div>
                                    {/* --------------------------- */}
                                  </div>

                                  <div className="files-interaction-panel">
                                    <h4><i className="fas fa-layer-group"></i> Property Documents & Floor Plans</h4>
                                    <div className="uploaded-files-grid">
                                      {(item.uploadedFiles || []).map((file) => (
                                        <button type="button" key={file._id} className="file-chip-btn" onClick={() => downloadFile(item._id, file)}>
                                          <i className="fas fa-file-download"></i>
                                          <span>{file.originalName.length > 25 ? file.originalName.substring(0,25)+'...' : file.originalName}</span>
                                        </button>
                                      ))}
                                    </div>
                                    <div className="upload-new-files">
                                      <label>Upload Additional Files</label>
                                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => setSelectedFiles({ ...selectedFiles, [item._id]: Array.from(e.target.files || []) })} style={{ flexGrow: 1 }} />
                                        <button type="button" className="secondary-action-btn" onClick={() => uploadMoreFiles(item._id)} disabled={uploadingId === item._id || !(selectedFiles[item._id]?.length > 0)} style={{ padding: '0.8rem 1.5rem', whiteSpace: 'nowrap', borderColor: 'var(--vastu-green)', color: 'var(--vastu-green)' }}>
                                          {uploadingId === item._id ? 'Uploading...' : 'Upload'}
                                        </button>

                                        {/* --- NEW BUTTON: NOTIFY CONSULTANT (FILES) --- */}
                                        {!['completed', 'report_uploaded', 'cancelled', 'refunded', 'under_review'].includes(item.status) && (
                                          <button
                                            type="button"
                                            className="primary-btn"
                                            onClick={() => handleNotifyConsultant(item._id)}
                                            disabled={isNotifyingId === item._id}
                                            style={{ padding: '0.8rem 1.5rem', whiteSpace: 'nowrap', background: '#f59e0b' }}
                                            title="Click this after uploading files to alert the consultant"
                                          >
                                            <i className="fas fa-bell"></i> {isNotifyingId === item._id ? 'Notifying...' : 'Notify New Information Submitted'}
                                          </button>
                                        )}
                                        {/* --------------------------------------------- */}

                                      </div>
                                    </div>
                                  </div>
                              </div>
                          )}

                          {/* ==================================== */}
                          {/* TAB 2: DISCUSSION THREAD             */}
                          {/* ==================================== */}
                          {activeTab === 'chat' && (
                              <div className="fade-in chat-container">
                                  
                                  {/* --- NEW BUTTON: NOTIFY BANNER IN CHAT --- */}
                                  {!['completed', 'report_uploaded', 'cancelled', 'refunded', 'under_review'].includes(item.status) && (
                                    <div style={{ background: '#fffbeb', padding: '10px 15px', borderBottom: '1px solid #fde68a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '0.9rem', color: '#92400e' }}>
                                        <i className="fas fa-info-circle"></i> Waiting for consultant? If you've replied to their questions, click here:
                                      </span>
                                      <button 
                                        onClick={() => handleNotifyConsultant(item._id)}
                                        disabled={isNotifyingId === item._id}
                                        style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}
                                      >
                                        <i className="fas fa-bell"></i> {isNotifyingId === item._id ? 'Notifying...' : 'Notify Consultant'}
                                      </button>
                                    </div>
                                  )}
                                  {/* ----------------------------------------- */}

                                  <div className="chat-messages-area">
                                      {(item.messages || []).length === 0 ? (
                                          <p className="empty-chat-msg">No messages yet. Send a message to your consultant below.</p>
                                      ) : (
                                          item.messages.map((msg, idx) => {
                                              const isMe = msg.senderId === user?.id;
                                              return (
                                                  <div key={idx} className={`chat-message ${isMe ? 'msg-mine' : 'msg-theirs'}`}>
                                                      <div className="msg-bubble">
                                                          <div className="msg-sender">{msg.senderName} <small>({msg.senderRole})</small></div>
                                                          {msg.text && <p className="msg-text">{msg.text}</p>}
                                                          {msg.attachment && (
                                                              <button type="button" className="msg-attachment" onClick={() => downloadFile(item._id, msg.attachment)}>
                                                                  <i className="fas fa-image"></i> View Attachment
                                                              </button>
                                                          )}
                                                          <div className="msg-time">{new Date(msg.sentAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</div>
                                                      </div>
                                                  </div>
                                              )
                                          })
                                      )}
                                  </div>
                                  <div className="chat-input-area">
                                      <textarea 
                                          placeholder="Type your message here..." 
                                          rows="2"
                                          value={messageDrafts[item._id]?.text || ''}
                                          onChange={(e) => handleMessageDraftChange(item._id, 'text', e.target.value)}
                                      ></textarea>
                                      <div className="chat-controls">
                                          <input 
                                              type="file" 
                                              accept="image/*" 
                                              id={`chat-img-${item._id}`}
                                              style={{ display: 'none' }}
                                              onChange={(e) => handleMessageDraftChange(item._id, 'file', e.target.files[0])}
                                          />
                                          <label htmlFor={`chat-img-${item._id}`} className={`chat-attach-btn ${messageDrafts[item._id]?.file ? 'has-file' : ''}`}>
                                              <i className="fas fa-paperclip"></i> {messageDrafts[item._id]?.file ? 'File Attached' : 'Attach Image'}
                                          </label>
                                          
                                          <button 
                                              className="primary-btn" 
                                              onClick={() => sendMessage(item._id)}
                                              disabled={sendingMessageId === item._id || (!messageDrafts[item._id]?.text?.trim() && !messageDrafts[item._id]?.file)}
                                          >
                                              {sendingMessageId === item._id ? 'Sending...' : 'Send'} <i className="fas fa-paper-plane"></i>
                                          </button>
                                      </div>
                                  </div>
                              </div>
                          )}

                          {/* ==================================== */}
                          {/* TAB 3: PAYMENT                       */}
                          {/* ==================================== */}
                          {activeTab === 'payment' && (
                              <div className="fade-in">
                                  <div className="payment-status-banner">
                                      <h3>Payment Status: <span className={`status-text-${item.payment?.status}`}>{item.payment?.status.toUpperCase()}</span></h3>
                                      <p>Total Fee: <strong>{formatCurrency(item.payment?.amount || 0)}</strong></p>
                                  </div>

                                  {item.payment?.mode === 'consultant_manual' && ['pending', 'submitted', 'rejected'].includes(item.payment?.status) && (
                                    <div className="payment-interaction-panel">
                                      <div className="payment-instructions">
                                        <h4><i className="fas fa-wallet"></i> Instructions from Consultant</h4>
                                        {!item.paymentInstructions?.upiId && !item.paymentInstructions?.paymentLink && !item.paymentInstructions?.qrCodeFile ? (
                                          <p className="waiting-text">Awaiting payment details from the consultant. Please check back later.</p>
                                        ) : (
                                          <div className="instruction-details">
                                            {item.paymentInstructions?.amount && <p className="payment-amount-highlight">Please pay: {formatCurrency(item.paymentInstructions.amount)}</p>}
                                            {item.paymentInstructions?.upiId && <p><strong>UPI ID:</strong> {item.paymentInstructions.upiId}</p>}
                                            {item.paymentInstructions?.paymentLink && <p><strong>Link:</strong> <a href={item.paymentInstructions.paymentLink} target="_blank" rel="noreferrer">Open Payment Link</a></p>}
                                            {item.paymentInstructions?.notes && <p className="payment-notes">"{item.paymentInstructions.notes}"</p>}
                                            {qrPreviewUrls[item._id] && (
                                              <div className="qr-gallery-view">
                                                <img src={qrPreviewUrls[item._id]} alt="Payment QR Code" />
                                                <p>Scan to Pay</p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {(item.status === 'payment_pending' || ['pending', 'rejected'].includes(item.payment?.status) || item.paymentProof?.verificationStatus === 'rejected') && (
                                        <div className="payment-proof-upload">
                                          <h4><i className="fas fa-upload"></i> Submit Payment Proof</h4>
                                          {item.paymentProof?.verificationStatus === 'rejected' && (
                                            <div className="rejection-notice">
                                              <strong>Previous proof rejected.</strong> Reason: {item.paymentProof.rejectionReason}
                                            </div>
                                          )}
                                          <div className="proof-form-group">
                                            <label>Transaction ID / UTR</label>
                                            <input type="text" value={proofDrafts[item._id]?.transactionReference || ''} onChange={(e) => updateProofDraft(item._id, 'transactionReference', e.target.value)} placeholder="e.g. 123456789012" />
                                          </div>
                                          <div className="proof-form-group">
                                            <label>Screenshot (Optional but recommended)</label>
                                            <input type="file" accept="image/jpeg, image/png" onChange={(e) => updateProofDraft(item._id, 'proofFile', e.target.files?.[0] || null)} />
                                          </div>
                                          <button type="button" className="primary-btn" onClick={() => submitPaymentProof(item._id)} disabled={submittingProofId === item._id}>
                                            {submittingProofId === item._id ? 'Submitting...' : 'Submit Proof for Verification'}
                                          </button>
                                        </div>
                                      )}
                                      
                                      {item.payment?.status === 'submitted' && (
                                         <div className="waiting-text" style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
                                           <i className="fas fa-clock"></i> Your payment proof has been submitted and is awaiting verification by the consultant.
                                         </div>
                                      )}
                                    </div>
                                  )}
                              </div>
                          )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      <ReviewModal 
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        consultation={consultationToReview}
        onSubmit={handleSubmitReview}
      />
    </div>
  );
};

export default MyConsultationsPage;